import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  getS7CommServerConfigAudit,
  getS7CommServerConfig,
  getS7CommServerRevisions,
  getS7CommServerStatus,
  previewS7CommServerConfig,
  previewS7CommServerRollback,
  repairS7CommServer,
  restartS7CommServer,
  rollbackS7CommServerRevision,
  updateS7CommServerConfig,
} from "../../../api";
import chevronIcon from "../../../assets/chevron-right.svg";
import Select from "../../../components/Select";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";
import { formatProtocolServerDateTime } from "./plc-protocol-servers-model";
import {
  buildS7ServerPayload,
  createS7DataBlock,
  createS7Field,
  normalizeS7RevisionHistory,
  normalizeS7ConfigAudit,
  normalizeS7RollbackPreview,
  normalizeS7ServerStatus,
  s7RollbackBlockerLabel,
  s7RollbackSuccessNotice,
  s7ServerFormFromConfig,
  validateS7ServerForm,
} from "./plc-s7comm-server-model";
import {
  FieldError,
  NumberField,
  Metric,
  QualityRejection,
  ReviewModal,
  RollbackReviewModal,
  S7StatusPill,
  S7DataBlockEditor,
  formatDateTime,
  displayNumber,
  configAuditActionLabel,
  configAuditOutcomeLabel,
} from "./plc-s7comm-server.parts";

const STATUS_POLL_MS = 5000;

export const S7CommServerConfiguration = ({ active = true, showHeader = true }) => {
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [history, setHistory] = useState({ profile: "s7comm-server-v1", revisions: [] });
  const [historyError, setHistoryError] = useState("");
  const [configAudit, setConfigAudit] = useState({ profile: "s7comm-server-v1", operations: [] });
  const [configAuditError, setConfigAuditError] = useState("");
  const [rollbackPreviewing, setRollbackPreviewing] = useState(0);
  const [rollbackReview, setRollbackReview] = useState(null);
  const [rollbackAcknowledged, setRollbackAcknowledged] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [configLoadFailed, setConfigLoadFailed] = useState(false);
  const [repairReason, setRepairReason] = useState("");
  const [repairAcknowledged, setRepairAcknowledged] = useState(false);
  const [review, setReview] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const notify = useNotify();
	const loadedRef = React.useRef(false);
  const canConfigure = usePermission("configure");

  const payload = useMemo(() => form ? buildS7ServerPayload(form) : null, [form]);
  const fingerprint = useMemo(() => payload ? JSON.stringify(payload) : "", [payload]);
  const dirty = !!baseline && fingerprint !== baseline;
  const baselinePayload = useMemo(() => {
    try { return baseline ? JSON.parse(baseline) : null; } catch { return null; }
  }, [baseline]);
  const validation = useMemo(
    () => form ? validateS7ServerForm(form) : { valid: false, message: "Configuration has not loaded.", formErrors: {}, dataBlockErrors: [], fieldErrors: [] },
    [form]
  );
  const editable = !!form && canConfigure && !saving && !restarting && !repairing && !rollingBack;
  const activationAvailable = status?.executable === true;
  const repairAvailable = configLoadFailed && status?.repairRequired === true;
  const trimmedRepairReason = repairReason.trim();
  const repairReasonValid = trimmedRepairReason.length >= 10 && trimmedRepairReason.length <= 512;

  const load = useCallback(async ({ preserveForm = false } = {}) => {
    setLoading(true);
    setLoadError("");
    setHistoryError("");
    setConfigAuditError("");
    setConfigLoadFailed(false);
    const [configResult, statusResult, historyResult, configAuditResult] = await Promise.allSettled([
      getS7CommServerConfig(),
      getS7CommServerStatus(),
      getS7CommServerRevisions(50),
      getS7CommServerConfigAudit(50),
    ]);
    if (statusResult.status === "fulfilled") setStatus(normalizeS7ServerStatus(statusResult.value));
    if (historyResult.status === "fulfilled") {
      setHistory(normalizeS7RevisionHistory(historyResult.value));
    } else {
      setHistoryError("Saved configurations could not be loaded. Try Refresh list again.");
    }
    if (configAuditResult.status === "fulfilled") {
      setConfigAudit(normalizeS7ConfigAudit(configAuditResult.value));
    } else {
      setConfigAuditError("The change log could not be loaded. Try Refresh log again.");
    }
    if (configResult.status === "fulfilled") {
      const nextForm = s7ServerFormFromConfig(configResult.value);
      const nextBaseline = JSON.stringify(buildS7ServerPayload(nextForm));
      if (!preserveForm) setForm(nextForm);
      setBaseline(nextBaseline);
    } else {
	  if (!preserveForm) {
		setForm(null);
		setBaseline("");
	  }
      setConfigLoadFailed(true);
      setLoadError("The S7Comm server settings could not be loaded.");
    }
    if (statusResult.status === "rejected") {
      setLoadError("The S7Comm server status could not be loaded.");
    }
    setLoading(false);
  }, []);

  const refreshHistory = useCallback(async () => {
    const next = normalizeS7RevisionHistory(await getS7CommServerRevisions(50));
    setHistory(next);
    setHistoryError("");
    return next;
  }, []);

  const refreshConfigAudit = useCallback(async () => {
    const next = normalizeS7ConfigAudit(await getS7CommServerConfigAudit(50));
    setConfigAudit(next);
    setConfigAuditError("");
    return next;
  }, []);

  useEffect(() => {
	if (!active) return;
	load({ preserveForm: loadedRef.current }).finally(() => { loadedRef.current = true; });
  }, [active, load]);
  useEffect(() => {
	if (!active) return undefined;
    let cancelled = false;
    const timer = window.setInterval(() => {
      getS7CommServerStatus()
        .then((next) => { if (!cancelled) setStatus(normalizeS7ServerStatus(next)); })
        .catch(() => {});
    }, STATUS_POLL_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [active]);

  const setField = (property) => (event) => {
    const value = typeof event === "boolean" ? event : event.target.value;
    setForm((current) => ({ ...current, [property]: value }));
  };
  const changeDataBlock = (index, patch) => setForm((current) => ({
    ...current,
    dataBlocks: current.dataBlocks.map((db, candidate) => candidate === index ? { ...db, ...patch } : db),
  }));
  const removeDataBlock = (index) => setForm((current) => ({ ...current, dataBlocks: current.dataBlocks.filter((_, candidate) => candidate !== index) }));
  const addDataBlock = () => setForm((current) => {
    const used = new Set(current.dataBlocks.map((db) => Number(db.number)));
    let number = 1;
    while (used.has(number) && number < 65535) number += 1;
    return { ...current, dataBlocks: [...current.dataBlocks, createS7DataBlock({ number })] };
  });
  const changeField = (dbIndex, fieldIndex, patch) => setForm((current) => ({
    ...current,
    dataBlocks: current.dataBlocks.map((db, candidate) => candidate === dbIndex ? {
      ...db,
      fields: db.fields.map((field, fieldCandidate) => fieldCandidate === fieldIndex
        ? { ...field, ...patch, ...(patch.type && patch.type !== "BOOL" ? { bitOffset: "0" } : {}) }
        : field),
    } : db),
  }));
  const removeField = (dbIndex, fieldIndex) => setForm((current) => ({
    ...current,
    dataBlocks: current.dataBlocks.map((db, candidate) => candidate === dbIndex
      ? { ...db, fields: db.fields.filter((_, fieldCandidate) => fieldCandidate !== fieldIndex) }
      : db),
  }));
  const addField = (dbIndex) => setForm((current) => ({
    ...current,
    dataBlocks: current.dataBlocks.map((db, candidate) => candidate === dbIndex
      ? { ...db, fields: [...db.fields, createS7Field()] }
      : db),
  }));

  const applyPreview = async (preview, acknowledge = false) => {
    setSaving(true);
    try {
      if (!/^[0-9a-f]{32}$/.test(String(preview?.proposal_id || ""))) {
        throw new Error("The reviewed preview is missing its server-issued proposal identity. Preview again before saving.");
      }
      const confirmation = {
        proposal_id: preview.proposal_id,
        ...(preview.confirmation_required ? {
          digest: preview.digest,
          acknowledge_no_authentication: acknowledge,
        } : {}),
      };
      const updated = await updateS7CommServerConfig(preview.config || payload, confirmation);
      const nextForm = s7ServerFormFromConfig(updated);
      setForm(nextForm);
      setBaseline(JSON.stringify(buildS7ServerPayload(nextForm)));
      const [statusResult, historyResult, auditResult] = await Promise.allSettled([
        getS7CommServerStatus(),
        getS7CommServerRevisions(50),
        getS7CommServerConfigAudit(50),
      ]);
      if (statusResult.status === "fulfilled") setStatus(normalizeS7ServerStatus(statusResult.value));
      if (historyResult.status === "fulfilled") {
        setHistory(normalizeS7RevisionHistory(historyResult.value));
        setHistoryError("");
      } else {
        setHistoryError("The settings were saved, but saved configurations could not be refreshed yet.");
      }
      if (auditResult.status === "fulfilled") {
        setConfigAudit(normalizeS7ConfigAudit(auditResult.value));
        setConfigAuditError("");
      } else {
        setConfigAuditError("The settings were saved, but the change log could not be refreshed yet.");
      }
      setReview(null);
      setAcknowledged(false);
      notify({
        type: "success",
        title: updated.enabled ? "S7Comm server updated" : "S7Comm settings saved",
        message: updated.enabled
          ? `The S7Comm server is active at ${updated.bind_address}:${updated.port}.`
          : activationAvailable
            ? "The settings were saved and the S7Comm server remains off."
            : "The settings were saved here. No S7Comm server was started.",
      });
    } catch (error) {
      notify({ type: "error", title: "S7Comm settings not saved", message: "The server could not apply the reviewed settings. Reopen the review and try again." });
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!validation.valid) {
      notify({ type: "error", title: "S7Comm configuration needs attention", message: validation.message });
      return;
    }
    setSaving(true);
    try {
      const preview = await previewS7CommServerConfig(payload);
      if (!/^[0-9a-f]{32}$/.test(String(preview?.proposal_id || ""))) {
        throw new Error("The server did not return a bounded proposal identity. Refresh and preview again.");
      }
      const exposureChanges = payload.enabled || baselinePayload?.enabled;
      if (preview.confirmation_required || exposureChanges) {
        setReview(preview);
        setAcknowledged(false);
        setSaving(false);
        return;
      }
      setSaving(false);
      await applyPreview(preview, false);
    } catch (error) {
      setSaving(false);
      notify({ type: "error", title: "S7Comm review unavailable", message: "The server could not validate these settings. Check the form, refresh the page, and try again." });
    }
  };

  const restart = async () => {
    setRestarting(true);
    try {
      const next = normalizeS7ServerStatus(await restartS7CommServer());
      setStatus(next);
      notify({
        type: next.runtime.listening || !next.runtime.enabled ? "success" : "warning",
        title: next.runtime.listening ? "S7Comm listener restarted" : "S7Comm listener remains stopped",
        message: next.runtime.listening
          ? `Listening on ${next.runtime.bindAddress}:${next.runtime.port}.`
          : "The saved settings keep the S7Comm server off. Check the status panel if you expected it to be listening.",
      });
    } catch (error) {
      notify({ type: "error", title: "S7Comm restart failed", message: "The server could not restart from its saved settings. Refresh the page and check the status panel." });
    } finally {
      setRestarting(false);
    }
  };

  const repairDisabled = async () => {
    if (!repairReasonValid || !repairAcknowledged) {
      notify({
        type: "error",
        title: "Recovery confirmation required",
        message: "Provide a 10–512 character reason and acknowledge that the listener and current desired/applied state will be replaced.",
      });
      return;
    }
    setRepairing(true);
    try {
      const result = await repairS7CommServer(trimmedRepairReason, true);
      const nextForm = s7ServerFormFromConfig(result.config);
      setForm(nextForm);
      setBaseline(JSON.stringify(buildS7ServerPayload(nextForm)));
      const nextStatus = normalizeS7ServerStatus(result.status);
      setStatus(nextStatus);
      setLoadError("");
      setConfigLoadFailed(false);
      setRepairReason("");
      setRepairAcknowledged(false);
      try {
        await Promise.all([refreshHistory(), refreshConfigAudit()]);
      } catch (error) {
        setHistoryError("The settings were repaired, but saved configurations could not be refreshed yet.");
      }
      notify({
        type: "success",
        title: "S7Comm state repaired and disabled",
        message: "The unreadable settings were replaced with a blank configuration and the S7Comm server remains off.",
      });
    } catch (error) {
      notify({
        type: "error",
        title: "S7Comm recovery failed",
        message: "The repair did not complete. Keep the S7Comm server off and try again.",
      });
    } finally {
      setRepairing(false);
    }
  };

  const previewRollback = async (revision) => {
    setRollbackPreviewing(revision);
    try {
      const preview = normalizeS7RollbackPreview(await previewS7CommServerRollback(revision));
      if (preview.targetRevision !== revision || !preview.targetDigest || !preview.baseRevision || !preview.digest || !preview.confirmationRequired) {
        throw new Error("Rollback preview identity is incomplete or does not match the selected revision.");
      }
      setRollbackReview(preview);
      setRollbackAcknowledged(false);
    } catch (error) {
      notify({
        type: "error",
        title: "Saved settings could not be reviewed",
        message: "The server could not prepare these saved settings. Refresh the list and try again.",
      });
    } finally {
      setRollbackPreviewing(0);
    }
  };

  const confirmRollback = async () => {
    if (!rollbackReview) return;
    if (rollbackReview.noAuthenticationAcknowledgementRequired && !rollbackAcknowledged) {
      notify({
        type: "error",
        title: "Acknowledgement required",
        message: "Acknowledge the unauthenticated S7Comm exposure before restoring these settings.",
      });
      return;
    }
    setRollingBack(true);
    try {
      const result = await rollbackS7CommServerRevision(rollbackReview.targetRevision, {
        base_revision: rollbackReview.baseRevision,
        digest: rollbackReview.digest,
        acknowledge_no_authentication: rollbackAcknowledged,
      });
      const nextForm = s7ServerFormFromConfig(result.config);
      setForm(nextForm);
      setBaseline(JSON.stringify(buildS7ServerPayload(nextForm)));
      const nextStatus = normalizeS7ServerStatus(result.status);
      setStatus(nextStatus);
      setRollbackReview(null);
      setRollbackAcknowledged(false);
      try {
        await Promise.all([refreshHistory(), refreshConfigAudit()]);
      } catch (error) {
        setHistoryError("The settings were restored, but saved configurations could not be refreshed yet.");
      }
      notify(s7RollbackSuccessNotice(rollbackReview.targetRevision, result.config?.revision, nextStatus));
    } catch (error) {
      notify({
        type: "error",
        title: "S7Comm settings not restored",
        message: "The settings changed after this review or could not be applied. Reopen the review and try again.",
      });
    } finally {
      setRollingBack(false);
    }
  };

  const runtime = status?.runtime || {};
  const metrics = runtime.metrics || {};
  const showRuntimeMetrics = Boolean(runtime.listening || [
    metrics.activeClients,
    metrics.acceptedSessions,
    metrics.rejectedByCIDR,
    metrics.readRequests,
    metrics.writeRequests,
    metrics.protocolErrors,
  ].some((value) => Number(value) > 0));

  return (
    <section className="gwFormStack plcS7Server" data-gw-resource="s7comm_server">
      {showHeader ? <div className="gwPageHeader plcOpcuaHeader">
        <div>
          <div className="plcOpcuaTitle">S7Comm server</div>
          <div className="gwPageSubtitle">
            Let Siemens S7 clients read or write selected Control Seat tags.
          </div>
        </div>
        <div className="gwHeaderActions">
          <button className="gwSecondaryBtn" type="button" disabled={loading || repairing || rollingBack || rollbackPreviewing > 0} onClick={() => load({ preserveForm: dirty })}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button className="gwSecondaryBtn" type="button" disabled={!activationAvailable || !canConfigure || restarting || saving || repairing || rollingBack || rollbackPreviewing > 0} onClick={restart}>
            {restarting ? "Restarting…" : "Restart listener"}
          </button>
        </div>
      </div> : null}

      {loading && !status && !form ? <div className="gwHint">Loading S7Comm…</div> : null}

      {loadError ? (
        <div className="gwAlert" role="alert">
          <div className="gwAlertTitle">S7Comm server unavailable</div>
          <div className="gwAlertBody">Refresh the page and try again.</div>
        </div>
      ) : null}

      {repairAvailable ? (
        <div className="gwCardSection plcS7RepairCard" data-gw-resource="s7comm_server_repair">
          <div className="gwSectionTitle"><span>Repair server settings</span></div>
          <div className="gwCardSectionBody">
            <div className="plcProtocolExposureWarning" role="alert">
              <strong>The saved settings cannot be read</strong>
              <span>Repair turns off S7Comm and replaces the unreadable settings with a new blank configuration.</span>
            </div>
            <div className="gwField">
              <label className="gwLabel" htmlFor="plc-s7-repair-reason">Reason for repair</label>
              <textarea
                id="plc-s7-repair-reason"
                className="gwTextarea"
                rows={3}
                minLength={10}
                maxLength={512}
                value={repairReason}
                disabled={!canConfigure || repairing}
                aria-invalid={repairReason.length > 0 && !repairReasonValid}
                onChange={(event) => setRepairReason(event.target.value)}
                placeholder="Describe why the unreadable state must be replaced"
              />
              <div className="gwHint">Required: 10–512 characters.</div>
              <FieldError>{repairReason.length > 0 && !repairReasonValid ? "Recovery reason must be 10–512 characters after trimming." : ""}</FieldError>
            </div>
            <label className="plcS7Acknowledge">
              <input
                type="checkbox"
                checked={repairAcknowledged}
                disabled={!canConfigure || repairing}
                onChange={(event) => setRepairAcknowledged(event.target.checked)}
              />
              <span>I understand this turns off S7Comm and replaces its unreadable settings with a blank configuration.</span>
            </label>
            {!canConfigure ? <div className="gwNotice isSoft">Configure permission is required to repair these settings.</div> : null}
            <div className="plcProtocolSaveRow">
              <div>
                <strong>Existing mappings will not be copied</strong>
                <span>After repair, re-create the server settings and tag mappings.</span>
              </div>
              <button
                className="gwDangerBtn"
                type="button"
                disabled={!canConfigure || repairing || !repairReasonValid || !repairAcknowledged}
                onClick={repairDisabled}
              >
                {repairing ? "Repairing…" : "Disable and repair state"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {status ? (
        <div className="gwCardSection plcServerOverviewCard">
          <div className="gwSectionTitle plcProtocolSectionTitle">
            <span>Status</span>
            <div className="plcProtocolSectionActions">
              <S7StatusPill status={status} />
              {!showHeader ? <>
                <button className="gwSecondaryBtn" type="button" disabled={loading || repairing || rollingBack || rollbackPreviewing > 0} onClick={() => load({ preserveForm: dirty })}>
                  {loading ? "Refreshing…" : "Refresh"}
                </button>
                <button className="gwSecondaryBtn" type="button" disabled={!activationAvailable || !canConfigure || restarting || saving || repairing || rollingBack || rollbackPreviewing > 0} onClick={restart}>
                  {restarting ? "Restarting…" : "Restart"}
                </button>
              </> : null}
            </div>
          </div>
          <div className="gwCardSectionBody plcServerOverviewBody">
            {!activationAvailable ? (
              <div className="plcServerLead" role="note">
                <strong>Open this page on the server computer</strong>
                <span>S7Comm must run on the computer that accepts the plant-network connection. Open Control Seat there to configure and start it.</span>
              </div>
            ) : null}
            <div className="plcServerFactGrid">
              <div><span>Connection</span><strong>{runtime.listening ? "Listening" : activationAvailable ? "Stopped" : "Not available here"}</strong></div>
              <div><span>Address</span><strong>{runtime.bindAddress && runtime.port ? `${runtime.bindAddress}:${runtime.port}` : "Not configured"}</strong></div>
              <div><span>Data blocks</span><strong>{runtime.dbCount}</strong></div>
              <div><span>Mapped tags</span><strong>{runtime.mappingCount}</strong></div>
            </div>
            {status.lifecycle === "degraded" || runtime.lifecycle === "degraded" ? (
              <div className="plcProtocolExposureWarning" role="alert">
                <strong>Some writes are being rejected</strong>
                <span>Reads remain available, but recent write activity needs attention. Review the connection diagnostics before relying on external writes.</span>
              </div>
            ) : null}
            {activationAvailable && showRuntimeMetrics ? <div className="plcProtocolMetrics" aria-label="S7Comm server metrics">
              <Metric label="Active clients" value={metrics.activeClients} />
              <Metric label="Accepted sessions" value={metrics.acceptedSessions} />
              <Metric label="Rejected by CIDR" value={metrics.rejectedByCIDR} />
              <Metric label="Reads" value={metrics.readRequests} />
              <Metric label="Writes" value={metrics.writeRequests} />
              <Metric label="Protocol errors" value={metrics.protocolErrors} />
            </div> : null}
            {activationAvailable && showRuntimeMetrics ? <details className="plcServerInlineDetails">
              <summary><span>Runtime diagnostics</span><small>Sessions, protocol results, writes, and quality conversion</small><img src={chevronIcon} alt="" className="plcServerDisclosureChevron" aria-hidden="true" /></summary>
              <div className="plcS7Diagnostics" aria-label="S7Comm detailed diagnostics">
              <div className="plcS7DiagnosticGroup">
                <strong>Sessions and transport</strong>
                <div className="plcProtocolMetrics">
                  <Metric label="Awaiting COTP" value={metrics.handshakeAwaitCOTP} />
                  <Metric label="Awaiting setup" value={metrics.handshakeAwaitSetup} />
                  <Metric label="Active S7 sessions" value={metrics.handshakeActive} />
                  <Metric label="Requests / second" value={metrics.requestsPerSecond} />
                  <Metric label="Malformed frames" value={metrics.malformedFrames} />
                  <Metric label="Oversized PDUs" value={metrics.oversizedPDUs} />
                </div>
              </div>
              <div className="plcS7DiagnosticGroup">
                <strong>Per-item return results</strong>
                <div className="plcProtocolMetrics">
                  <Metric label="Success" value={metrics.itemReturns?.success} />
                  <Metric label="Denied" value={metrics.itemReturns?.denied} />
                  <Metric label="Address" value={metrics.itemReturns?.address} />
                  <Metric label="Unsupported" value={metrics.itemReturns?.unsupported} />
                  <Metric label="Type" value={metrics.itemReturns?.type} />
                  <Metric label="Internal" value={metrics.itemReturns?.internal} />
                </div>
              </div>
              <div className="plcS7DiagnosticGroup">
                <strong>Write stages</strong>
                <div className="plcProtocolMetrics">
                  <Metric label="Requested" value={metrics.writeStages?.requested} />
                  <Metric label="Ownership accepted" value={metrics.writeStages?.ownershipAccepted} />
                  <Metric label="Ownership rejected" value={metrics.writeStages?.ownershipRejected} />
                  <Metric label="Connector accepted" value={metrics.writeStages?.connectorAccepted} />
                  <Metric label="Changes logged" value={metrics.writeStages?.auditRecorded} />
                  <Metric label="Logging failures" value={metrics.writeStages?.auditFailures} />
                </div>
                <small>Independently observed: {displayNumber(metrics.writeStages?.independentlyObserved)}. Connector acceptance is not physical output verification.</small>
              </div>
              <div className="plcS7DiagnosticGroup">
                <strong>Quality conversion</strong>
                <div className="plcProtocolMetrics">
                  <Metric label="Good conversions" value={metrics.goodConversions} />
                  <Metric label="Rejected conversions" value={metrics.nonGoodConversions} />
                </div>
                {(metrics.qualityRejections || []).length ? (
                  <div className="plcS7QualityRows">
                    {metrics.qualityRejections.map((entry) => <QualityRejection entry={entry} key={entry.tagPath} />)}
                  </div>
                ) : <small>No mapped public tag path currently has a recorded read-quality rejection.</small>}
                <small>Structured source/edge disconnect causes are counted as Stale. Other non-Good values remain Non-Good; diagnostic text is never used to guess the cause.</small>
              </div>
              </div>
            </details> : null}
            <details className="plcServerInlineDetails">
              <summary><span>Connection details</span><small>Start time and important limitations</small><img src={chevronIcon} alt="" className="plcServerDisclosureChevron" aria-hidden="true" /></summary>
              <div className="plcServerDetailsBody">
                <div className="plcServerTechnicalRows">
                  <div><span>Started</span><strong>{formatProtocolServerDateTime(runtime.startedAt)}</strong></div>
                  <div><span>Read access</span><strong>Mapped tags only</strong></div>
                  <div><span>Write access</span><strong>Control Seat rules apply</strong></div>
                </div>
                <div className="plcServerDetailSection">
                  <strong>Writes</strong>
                  <span>S7Comm has no client sign-in or encryption. Limit access to trusted plant networks and expose only the tags clients need.</span>
                </div>
                {(status.warnings || []).length ? (
                  <div className="plcServerDetailSection"><strong>Warnings</strong><ul className="plcOpcuaWarningList">
                    {status.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                  </ul></div>
                ) : null}
              </div>
            </details>
          </div>
        </div>
      ) : null}

      {!loading && status ? <details className="gwCardSection plcServerDetails plcServerAdvanced">
        <summary className="gwSectionTitle plcProtocolSectionTitle">
          <div><span>Advanced</span><small>Compatibility, saved settings, and troubleshooting</small></div>
          <img src={chevronIcon} alt="" className="plcServerDisclosureChevron" aria-hidden="true" />
        </summary>
        <div className="gwCardSectionBody plcServerDetailsBody">
      <details className="plcServerInlineDetails">
        <summary className="gwSectionTitle plcProtocolSectionTitle">
          <div><span>Supported features</span><small>Available S7Comm operations</small></div>
          <img src={chevronIcon} alt="" className="plcServerDisclosureChevron" aria-hidden="true" />
        </summary>
        <div className="gwCardSectionBody plcServerDetailsBody">
          <div className="plcServerDetailSection">
            <strong>Supported client operations</strong>
            <span>Clients can read and write mapped data-block values using BOOL, BYTE, WORD, INT, DWORD, DINT, and REAL.</span>
          </div>
          <div className="plcServerDetailSection">
            <strong>Not supported</strong>
            <span>Device discovery, PLC program transfer, symbolic access, and non-data-block areas are not available.</span>
          </div>
        </div>
      </details>

      <details className="gwCardSection plcServerDetails" data-gw-resource="s7comm_server_revisions">
        <summary className="gwSectionTitle plcProtocolSectionTitle">
          <div>
            <span>Saved configurations</span>
            <small>{history.revisions.length} saved · newest first</small>
          </div>
          <img src={chevronIcon} alt="" className="plcServerDisclosureChevron" aria-hidden="true" />
        </summary>
        <div className="gwCardSectionBody plcServerDetailsBody">
          <div className="plcServerDetailsActions">
          <button
            className="gwSecondaryBtn"
            type="button"
            disabled={loading || rollingBack || rollbackPreviewing > 0}
            onClick={() => refreshHistory().catch(() => setHistoryError("Saved configurations could not be refreshed. Try again."))}
          >
            Refresh list
          </button>
          </div>
          <div className="plcServerDetailSection">
            {/* Was: "previews the semantic difference against the current
                durable base, then appends a new higher revision after exact
                digest-bound review." Same guarantee, said once, in words an
                operator uses. */}
            Restoring does not delete newer settings. Review the changes first, then restore the configuration you selected.
          </div>
          {historyError ? (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">Saved configurations unavailable</div>
              <div className="gwAlertBody">{historyError}</div>
            </div>
          ) : null}
          {history.revisions.length ? (
            <div className="plcS7RevisionList">
              {history.revisions.map((revision) => (
                <div className="plcS7RevisionRow" key={revision.revision}>
                  <div className="plcS7RevisionMain">
                    <div className="plcS7RevisionTitle">
                      <strong>{revision.desired ? "Selected configuration" : "Saved configuration"}</strong>
                      {revision.applied ? <span className="plcS7RevisionBadge isApplied">In use</span> : null}
                      {!revision.sealed ? <span className="plcS7RevisionBadge isUnsealed">Cannot restore</span> : null}
                    </div>
                    <div className="plcS7RevisionFacts">
                      <span>{revision.enabled ? "Enabled" : "Disabled"}</span>
                      <span>{revision.bindAddress}:{revision.port}</span>
                      <span>TSAP {revision.calledTSAP || "—"}</span>
                      <span>{revision.dataBlockCount} DBs · {revision.mappingCount} mappings · {revision.writableMappingCount} writable</span>
                      <span>Created {formatDateTime(revision.createdAt)}</span>
                      <span>{revision.appliedAt ? `Applied ${formatDateTime(revision.appliedAt)}` : "Never applied"}</span>
                      {!revision.rollbackEligible && revision.rollbackBlocker ? <span>{s7RollbackBlockerLabel(revision.rollbackBlocker)}</span> : null}
                    </div>
                  </div>
                  <button
                    className="gwSecondaryBtn"
                    type="button"
                    disabled={!canConfigure || !revision.rollbackEligible || rollbackPreviewing > 0 || rollingBack || saving || repairing}
                    title={revision.rollbackEligible ? "Review these saved settings" : s7RollbackBlockerLabel(revision.rollbackBlocker)}
                    onClick={() => previewRollback(revision.revision)}
                  >
                    {rollbackPreviewing === revision.revision ? "Preparing…" : "Review restore"}
                  </button>
                </div>
              ))}
            </div>
          ) : historyError ? null : (
            <div className="gwEmptyState gwEmptyCompact">
              <div className="gwEmptyTitle">No saved configurations yet</div>
              <div className="gwEmptyBody">Save the S7Comm settings to create the first restorable configuration.</div>
            </div>
          )}
          {!canConfigure ? <div className="gwNotice isSoft">Configure permission is required to restore saved settings.</div> : null}
        </div>
      </details>

      <details className="gwCardSection plcServerDetails" data-gw-resource="s7comm_server_config_audit">
        <summary className="gwSectionTitle plcProtocolSectionTitle">
          <div>
            <span>Change log</span>
            <small>{configAudit.operations.length} loaded · newest first</small>
          </div>
          <img src={chevronIcon} alt="" className="plcServerDisclosureChevron" aria-hidden="true" />
        </summary>
        <div className="gwCardSectionBody plcServerDetailsBody">
          <div className="plcServerDetailsActions">
          <button
            className="gwSecondaryBtn"
            type="button"
            disabled={loading}
            onClick={() => refreshConfigAudit().catch(() => setConfigAuditError("The change log could not be refreshed. Try again."))}
          >
            Refresh log
          </button>
          </div>
          <div className="plcServerDetailSection">
            {/* Four sentences of the internal security model — "bounded
                operator summaries", "proposal-time evidence", "stable
                authenticated user identity" — none of which an operator needs
                to read a change log. Keep the three facts that affect them:
                what it is, who can see it, what it never contains. */}
            Who changed what, and when. Read permission shows this list; Configure permission is still required to make a change. Tag values, secrets, and configuration contents are never recorded here.
          </div>
          {configAuditError ? (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">Change log unavailable</div>
              <div className="gwAlertBody">{configAuditError}</div>
            </div>
          ) : null}
          {configAudit.operations.length ? (
            <div className="plcS7RevisionList">
              {configAudit.operations.map((operation, index) => (
                <div className="plcS7RevisionRow" key={`${operation.proposedAt}-${operation.actorId}-${index}`}>
                  <div className="plcS7RevisionMain">
                    <div className="plcS7RevisionTitle">
                      <strong>{configAuditActionLabel(operation.action)}</strong>
                      <span className={`plcS7RevisionBadge ${operation.outcome === "applied" || operation.outcome === "desired_only" ? "isApplied" : operation.outcome === "rejected" || operation.outcome === "apply_failed" || operation.outcome === "expired" ? "isUnsealed" : "isDesired"}`}>
                        {configAuditOutcomeLabel(operation.outcome)}
                      </span>
                    </div>
                    <div className="plcS7RevisionFacts">
                      <span>Changed by {operation.actorName || "Control Seat user"}</span>
                      <span>Started {formatDateTime(operation.proposedAt)}</span>
                      <span>{operation.completedAt ? `Completed ${formatDateTime(operation.completedAt)}` : `Expires ${formatDateTime(operation.expiresAt)}`}</span>
                      <span>{operation.semanticChangeCount} changes · {operation.dataBlockCount} DBs · {operation.mappingCount} mappings</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : configAuditError ? null : (
            <div className="gwEmptyState gwEmptyCompact">
              <div className="gwEmptyTitle">No changes yet</div>
              <div className="gwEmptyBody">Saved S7Comm changes will appear here.</div>
            </div>
          )}
        </div>
      </details>
        </div>
      </details> : null}

      {form ? (
        <>
          <div className="gwCardSection">
            <div className="gwSectionTitle plcProtocolSectionTitle">
              <span>Server settings</span>
              {dirty ? <span className="plcProtocolUnsaved">Unsaved changes</span> : null}
            </div>
            <div className="gwCardSectionBody">
              <div className="gwToggleRow plcProtocolEnableRow">
                <div>
                  <div className="gwLabel">Enable S7Comm server</div>
                  <div className="gwHint">Turn this on to let S7 clients connect after you save.</div>
                </div>
                <button
                  type="button"
                  className={`gwToggle ${form.enabled ? "isOn" : ""} ${!editable || !activationAvailable ? "isDisabled" : ""}`}
                  role="switch"
                  aria-checked={form.enabled}
                  aria-label="Enable S7Comm server"
                  disabled={!editable || !activationAvailable}
                  onClick={() => setField("enabled")(!form.enabled)}
                ><span /></button>
              </div>
              <div className="plcS7EndpointGrid">
                <div className="gwField plcProtocolListenHost">
                  <label className="gwLabel">Network address</label>
                  <input className="gwInput" value={form.bindAddress} onChange={setField("bindAddress")} disabled={!editable} aria-invalid={!!validation.formErrors.bindAddress} />
                  <div className="gwHint">Use the address on the network your S7 clients can reach.</div>
                  <FieldError>{validation.formErrors.bindAddress}</FieldError>
                </div>
                <NumberField label="TCP port" value={form.port} min={1} max={65535} step={1} onChange={setField("port")} disabled={!editable} error={validation.formErrors.port} hint="TCP/102 is production; 1102 is the safe unprivileged bench default." />
                <div className="gwField">
                  <label className="gwLabel">Port profile</label>
                  <Select
                    value={form.benchPort ? "bench" : "production"}
                    options={[
                      { value: "bench", label: "High bench port" },
                      { value: "production", label: "Production TCP/102" },
                    ]}
                    onChange={(value) => setField("benchPort")(value === "bench")}
                    disabled={!editable}
                  />
                </div>
                <div className="gwField plcS7CIDRs">
                  <label className="gwLabel">Allowed client networks</label>
                  <textarea className="gwTextarea" rows={3} value={form.sourceCIDRs} onChange={setField("sourceCIDRs")} disabled={!editable} aria-invalid={!!validation.formErrors.sourceCIDRs} placeholder={"10.40.0.0/24\n10.50.3.12/32"} />
                  <div className="gwHint">One network per line. S7Comm has no client authentication, so allow only trusted plant networks.</div>
                  <FieldError>{validation.formErrors.sourceCIDRs}</FieldError>
                </div>
                <div className="gwField">
                  <label className="gwLabel">Destination TSAP</label>
                  <input className="gwInput" value={form.calledTSAP} maxLength={4} onChange={setField("calledTSAP")} disabled={!editable} aria-invalid={!!validation.formErrors.calledTSAP} />
                  <div className="gwHint">Use the four-character hexadecimal TSAP expected by the client.</div>
                  <FieldError>{validation.formErrors.calledTSAP}</FieldError>
                </div>
                <div className="gwField">
                  <label className="gwLabel">Maximum message size</label>
                  <Select
                    value={String(form.pduCap)}
                    options={[
                      { value: "240", label: "240 bytes" },
                      { value: "480", label: "480 bytes" },
                      { value: "960", label: "960 bytes" },
                    ]}
                    onChange={(value) => setForm((current) => ({ ...current, pduCap: value }))}
                    disabled={!editable}
                  />
                  <FieldError>{validation.formErrors.pduCap}</FieldError>
                </div>
              </div>
            </div>
          </div>

          <details className="gwCardSection plcServerDetails">
            <summary className="gwSectionTitle plcProtocolSectionTitle">
              <div><span>Advanced limits and timeouts</span><small>Clients, rates, bursts, and deadlines</small></div>
              <img src={chevronIcon} alt="" className="plcServerDisclosureChevron" aria-hidden="true" />
            </summary>
            <div className="gwCardSectionBody plcS7LimitsGrid">
              <NumberField label="Maximum clients" value={form.maxClients} min={1} max={64} step={1} onChange={setField("maxClients")} disabled={!editable} error={validation.formErrors.maxClients} />
              <NumberField label="Items per request" value={form.maxItems} min={1} max={16} step={1} onChange={setField("maxItems")} disabled={!editable} error={validation.formErrors.maxItems} />
              <NumberField label="Accepts / second" value={form.acceptRate} min={1} max={20} step={1} onChange={setField("acceptRate")} disabled={!editable} error={validation.formErrors.acceptRate} />
              <NumberField label="Accept burst" value={form.acceptBurst} min={1} max={40} step={1} onChange={setField("acceptBurst")} disabled={!editable} error={validation.formErrors.acceptBurst} />
              <NumberField label="Requests / second" value={form.requestRate} min={1} max={1000} step={1} onChange={setField("requestRate")} disabled={!editable} error={validation.formErrors.requestRate} />
              <NumberField label="Request burst" value={form.requestBurst} min={1} max={2000} step={1} onChange={setField("requestBurst")} disabled={!editable} error={validation.formErrors.requestBurst} />
              <NumberField label="Writes / second" value={form.writeRate} min={1} max={100} step={1} onChange={setField("writeRate")} disabled={!editable} error={validation.formErrors.writeRate} />
              <NumberField label="Write burst" value={form.writeBurst} min={1} max={200} step={1} onChange={setField("writeBurst")} disabled={!editable} error={validation.formErrors.writeBurst} />
              <NumberField label="Handshake timeout (ms)" value={form.handshakeTimeoutMS} min={1000} max={30000} step={250} onChange={setField("handshakeTimeoutMS")} disabled={!editable} error={validation.formErrors.handshakeTimeoutMS} />
              <NumberField label="Setup timeout (ms)" value={form.setupTimeoutMS} min={1000} max={30000} step={250} onChange={setField("setupTimeoutMS")} disabled={!editable} error={validation.formErrors.setupTimeoutMS} />
              <NumberField label="Progress timeout (ms)" value={form.progressTimeoutMS} min={250} max={10000} step={250} onChange={setField("progressTimeoutMS")} disabled={!editable} error={validation.formErrors.progressTimeoutMS} />
              <NumberField label="Idle timeout (ms)" value={form.idleTimeoutMS} min={5000} max={3600000} step={1000} onChange={setField("idleTimeoutMS")} disabled={!editable} error={validation.formErrors.idleTimeoutMS} />
              <NumberField label="Write timeout (ms)" value={form.writeTimeoutMS} min={10} max={2000} step={10} onChange={setField("writeTimeoutMS")} disabled={!editable} error={validation.formErrors.writeTimeoutMS} />
              <NumberField label="Drain timeout (ms)" value={form.drainTimeoutMS} min={100} max={10000} step={100} onChange={setField("drainTimeoutMS")} disabled={!editable} error={validation.formErrors.drainTimeoutMS} />
            </div>
          </details>

          <div className="gwCardSection">
            <div className="gwSectionTitle plcProtocolSectionTitle">
              <div><span>Exposed data blocks</span><small>{form.dataBlocks.length} data block{form.dataBlocks.length === 1 ? "" : "s"}</small></div>
              <button className="gwSecondaryBtn plcProtocolAddButton" type="button" disabled={!editable} onClick={addDataBlock}>Add data block</button>
            </div>
            <div className="gwCardSectionBody">
              <div className="gwNotice isSoft">
                Match S7 data-block addresses to the Control Seat tags external clients can access.
              </div>
              {form.dataBlocks.length ? (
                <div className="plcProtocolMappings">
                  {form.dataBlocks.map((db, dbIndex) => (
                    <S7DataBlockEditor
                      key={db.key}
                      db={db}
                      dbIndex={dbIndex}
                      error={validation.dataBlockErrors[dbIndex]}
                      fieldErrors={validation.fieldErrors[dbIndex]}
                      disabled={!editable}
                      onChange={changeDataBlock}
                      onRemove={removeDataBlock}
                      onAddField={addField}
                      onChangeField={changeField}
                      onRemoveField={removeField}
                    />
                  ))}
                </div>
              ) : (
                <div className="gwEmptyState gwEmptyCompact">
                  <div className="gwEmptyTitle">No data blocks are shared</div>
                  <div className="gwEmptyBody">Add a data block, then map its fields to Control Seat tags.</div>
                </div>
              )}
              {!canConfigure ? <div className="gwNotice isSoft">Configure permission is required to edit this endpoint.</div> : null}
              <div className="plcProtocolSaveRow">
                <div>
                  <strong>{validation.valid ? "Ready to review" : "Configuration needs attention"}</strong>
                  <span>{validation.valid
                    ? activationAvailable
                      ? "Review the server settings and exposed tags before saving."
                      : "Open this page on the server computer to enable S7Comm."
                    : validation.message}</span>
                </div>
                <button className="gwPrimaryBtn" type="button" disabled={!editable || !dirty || !validation.valid} onClick={save}>
                  {saving ? "Preparing…" : "Review and save"}
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <ReviewModal
        preview={review}
        saving={saving}
        acknowledged={acknowledged}
        onAcknowledge={setAcknowledged}
        onCancel={() => { if (!saving) { setReview(null); setAcknowledged(false); } }}
        onConfirm={() => applyPreview(review, acknowledged)}
      />
      <RollbackReviewModal
        preview={rollbackReview}
        saving={rollingBack}
        acknowledged={rollbackAcknowledged}
        hasUnsavedChanges={dirty}
        onAcknowledge={setRollbackAcknowledged}
        onCancel={() => { if (!rollingBack) { setRollbackReview(null); setRollbackAcknowledged(false); } }}
        onConfirm={confirmRollback}
      />
    </section>
  );
};
